import { useState } from 'react';
import { execApi } from '../../../services/api';
import { monthlyReportLink } from './reportLink';

// The therapist WORK detail — the calc voucher (count × tariff = total), bonus gates,
// and the ledger of meetings behind the number. ONE shared component used by both the
// salary dashboard drawer and the accounting hub (name click), so they never drift.
// Read-only; editing lives elsewhere (salary = hours entry, accounting = comp config).

const ils = (n: number) => '₪' + (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num = (n: number, d = 0) => (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });

const KIND_HE: Record<string, string> = {
  intake: 'אינטייק', followup: 'מעקב', double: 'מעקב כפול', reintake: 'ריאינטייק',
};
const STATUS_HE: Record<string, string> = {
  done: 'בוצעה', noshow: 'no-show', canceled: 'בוטלה', active: 'מתוזמנת',
};

interface PayLine { label: string; count: number; minutes_each: number; rate_per_meeting: number; total: number; }
interface BonusGate { label: string; actual: number; threshold: number; passed: boolean; }
interface Bonus { relevant: boolean; eligible: boolean; pct: number; amount: number; gates: BonusGate[]; noshow_headroom: number | null; }
interface LedgerMeeting {
  day: string; time: string; kind: string; patient_type: string;
  patient_serial: string; clinic: string; status: string; slot_minutes: number; amount: number;
}
interface AdditionalItem { item_id: string; category: string; label: string | null; amount: number; }
export interface DetailTherapist {
  name: string;
  lines: PayLine[];
  worked_hours: number;
  worked_payment: number; noshow_payment: number;
  unscheduled_payment: number; unscheduled_hours: number;
  // Every other component that feeds `total_payment` — optional so the accounting-hub
  // caller (which casts) never breaks; each is only rendered when > 0 so the voucher
  // ALWAYS reconciles to the total. (Ohad 2026-07-23)
  non_clinical_payment?: number; non_clinical_hours?: number;
  additional_payments?: number; additional_items?: AdditionalItem[];
  total_payment: number; irregular_slots: number;
  bonus: Bonus;
  // The signed link to this therapist's own monthly report. Optional because the
  // accounting hub casts its own row into this type; where it is absent the button
  // simply does not render.
  report_token?: string | null;
}
const _CAT_HE: Record<string, string> = {
  insurance: 'ביטוח', refund: 'החזר', sim: 'סימולציה', hours: 'שעות נוספות', custom: 'אחר',
  cm_bonus: 'בונוס סטים', oncall: 'כוננות',
};

export function TherapistDetail({ therapist: t, month, isCurrent }: {
  therapist: DetailTherapist; month: string; isCurrent: boolean;
}) {
  const [ledger, setLedger] = useState<LedgerMeeting[] | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (ledger) return;
    setLoading(true);
    try {
      const res = await execApi.getSalaryLedger(month, t.name);
      setLedger(res.data as LedgerMeeting[]);
    } finally { setLoading(false); }
  };

  return (
    <div>
      <div className="text-xs font-medium text-gray-500 mb-2">שובר החישוב — כמות × תעריף לפגישה</div>
      <table className="text-sm mb-3">
        <tbody>
          {t.lines.map((ln, i) => (
            <tr key={i} className="text-gray-700">
              <td className="py-1 pl-6 whitespace-nowrap">{ln.label}</td>
              <td className="py-1 pl-3 tabular-nums text-left">{num(ln.count)}</td>
              <td className="py-1 pl-1 text-gray-500">×</td>
              <td className="py-1 pl-3 tabular-nums text-left">{ils(ln.rate_per_meeting)}</td>
              <td className="py-1 pl-2 text-gray-500 text-xs">({ln.minutes_each} דק׳)</td>
              <td className="py-1 pl-1 text-gray-500">=</td>
              <td className="py-1 tabular-nums text-left font-medium">{ils(ln.total)}</td>
            </tr>
          ))}
          <tr className="border-t border-gray-300 font-semibold text-gray-900">
            <td className="py-1.5">סה״כ פגישות</td>
            <td className="py-1.5 tabular-nums text-left">{num(t.lines.reduce((a, l) => a + l.count, 0))}</td>
            {/* Hours = actual DONE durations only (worked_hours) — no-show is not a worked hour.
                Matches the "שעות עבודה" column in the main table. (Ohad 2026-07-23) */}
            <td colSpan={4} className="py-1.5 pr-2 text-gray-500 text-xs font-normal">
              {num(t.worked_hours, 2)} שעות שבוצעו
            </td>
            <td className="py-1.5 tabular-nums text-left">{ils(t.worked_payment + t.noshow_payment)}</td>
          </tr>
          {/* ── Additions on top of the meetings subtotal — every component that feeds the
                total, so the voucher reconciles exactly and never looks like a math error. ── */}
          {t.unscheduled_payment > 0 && (
            <tr className="text-gray-700">
              <td className="py-1">שעות לא מתוזמנות ({num(t.unscheduled_hours, 2)} ש׳)</td>
              <td colSpan={5}></td>
              <td className="py-1 tabular-nums text-left">{ils(t.unscheduled_payment)}</td>
            </tr>
          )}
          {(t.non_clinical_payment ?? 0) > 0 && (
            <tr className="text-gray-700">
              <td className="py-1">שעות לא-קליניות ({num(t.non_clinical_hours ?? 0, 2)} ש׳)</td>
              <td colSpan={5}></td>
              <td className="py-1 tabular-nums text-left">{ils(t.non_clinical_payment ?? 0)}</td>
            </tr>
          )}
          {(t.additional_payments ?? 0) > 0 && (
            <tr className="text-gray-700">
              <td className="py-1">תשלומים נוספים</td>
              <td colSpan={5}></td>
              <td className="py-1 tabular-nums text-left">{ils(t.additional_payments ?? 0)}</td>
            </tr>
          )}
          {(t.additional_items ?? []).filter((it) => it.amount).map((it) => (
            <tr key={it.item_id} className="text-gray-700">
              <td className="py-1">{it.label || _CAT_HE[it.category] || it.category}</td>
              <td colSpan={5}></td>
              <td className="py-1 tabular-nums text-left">{ils(it.amount)}</td>
            </tr>
          ))}
          {t.bonus.eligible && (
            <tr className="text-green-700">
              <td className="py-1">בונוס ({t.bonus.pct}%)</td>
              <td colSpan={5}></td>
              <td className="py-1 tabular-nums text-left">{ils(t.bonus.amount)}</td>
            </tr>
          )}
          <tr className="border-t-2 border-gray-400 font-bold text-gray-900">
            <td className="py-1.5">סה״כ לתשלום <span className="text-gray-500 font-normal text-xs">(לפני מע״מ)</span></td>
            <td colSpan={5}></td>
            <td className="py-1.5 tabular-nums text-left">{ils(t.total_payment)}</td>
          </tr>
        </tbody>
      </table>

      {/* Directly under the voucher it closes, on the right where the labels sit
          (Ohad, 2026-07-28). The moment you decide the total is right is the moment
          you want to see the page the therapist himself will get. */}
      {t.report_token && (
        <div className="mb-4">
          <a href={monthlyReportLink(t.report_token)} target="_blank" rel="noreferrer"
             className="inline-block rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700">
            ↗ הדוח החודשי
          </a>
        </div>
      )}

      {t.bonus.relevant && (
        <div>
          <div className="text-xs font-medium text-gray-500 mb-1.5">שערי הבונוס {t.bonus.eligible ? '' : '— לא עבר'}</div>
          <div className="flex gap-4 flex-wrap text-xs">
            {t.bonus.gates.map((g, i) => (
              <span key={i} className={g.passed ? 'text-green-700' : 'text-red-600'}>
                {g.passed ? '✓' : '✗'} {g.label}: {num(g.actual, 2)}
                <span className="text-gray-500"> (סף {num(g.threshold, 0)})</span>
              </span>
            ))}
          </div>
          {isCurrent && !t.bonus.eligible && t.bonus.noshow_headroom !== null && (
            <div className="text-xs text-gray-500 mt-1.5">
              {t.bonus.noshow_headroom >= 0
                ? `מרווח: עוד ${t.bonus.noshow_headroom} no-show לפני סגירת השער`
                : `חרג ב-${Math.abs(t.bonus.noshow_headroom)} no-show — לא ניתן לתיקון החודש`}
            </div>
          )}
        </div>
      )}

      {t.irregular_slots > 0 && (
        <div className="text-xs text-amber-700 mt-3">
          {t.irregular_slots} פגישות עם סלוט שאינו התעריף ואינו כפול — שולמו כרגיל וסומנו לבדיקה.
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-gray-200">
        <button onClick={(e) => { e.stopPropagation(); toggle(); }}
          className="text-xs text-blue-700 hover:text-blue-900 font-medium">
          {open ? '▾' : '▸'} הפגישות שמאחורי המספר{ledger ? ` (${ledger.length})` : ''}
        </button>
        {open && (loading || !ledger
          ? <div className="text-xs text-gray-500 mt-2">טוען…</div>
          : ledger.length === 0
            ? <div className="text-xs text-gray-500 mt-2">אין פגישות.</div>
            : (
              <div className="mt-2 max-h-80 overflow-y-auto border border-gray-200 rounded bg-white">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr className="text-gray-500">
                      {['תאריך', 'שעה', 'סוג', 'מטופל', 'מזהה', 'קליניקה', 'סטטוס', 'סלוט', 'תשלום'].map((h) => (
                        <th key={h} className="text-right font-medium px-2 py-1.5">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.map((m, i) => (
                      <tr key={i} className={`border-t border-gray-100 ${m.amount === 0 ? 'text-gray-500' : 'text-gray-700'}`}>
                        <td className="px-2 py-1 tabular-nums">{m.day}</td>
                        <td className="px-2 py-1 tabular-nums">{m.time}</td>
                        <td className="px-2 py-1">{KIND_HE[m.kind] ?? m.kind}{m.kind === 'double' && <span className="text-blue-700"> ×2</span>}</td>
                        <td className="px-2 py-1">{m.patient_type === 'Youth' ? 'נוער' : 'מבוגר'}</td>
                        <td className="px-2 py-1 tabular-nums text-gray-500">{m.patient_serial || '—'}</td>
                        <td className="px-2 py-1">{m.clinic || '—'}</td>
                        <td className="px-2 py-1">{STATUS_HE[m.status] ?? m.status}</td>
                        <td className="px-2 py-1 tabular-nums">{m.slot_minutes} דק׳</td>
                        <td className="px-2 py-1 tabular-nums font-medium">{m.amount ? ils(m.amount) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    {(() => {
                      const doneMin = ledger.filter((m) => m.status === 'done').reduce((a, m) => a + (m.slot_minutes || 0), 0);
                      const allMin = ledger.reduce((a, m) => a + (m.slot_minutes || 0), 0);
                      const sumAmt = ledger.reduce((a, m) => a + (m.amount || 0), 0);
                      const hrs = (min: number) => Math.round(min / 60 * 100) / 100;
                      return (
                        <tr className="border-t-2 border-gray-300 bg-gray-50 font-medium text-gray-700 sticky bottom-0">
                          <td className="px-2 py-1.5" colSpan={7}>סה״כ · {hrs(doneMin)} שעות שבוצעו (מתוך {hrs(allMin)} מתוזמנות)</td>
                          <td className="px-2 py-1.5 tabular-nums">{num(allMin)} דק׳</td>
                          <td className="px-2 py-1.5 tabular-nums">{ils(sumAmt)}</td>
                        </tr>
                      );
                    })()}
                  </tfoot>
                </table>
              </div>
            ))}
      </div>
    </div>
  );
}
